import React, { useState, useEffect, useRef } from 'react';
import { Attachment, attachmentApi, subscriptionApi, isStorageOverageBlocked } from '../services/api';
import { MediaImage, MediaVideo, Page, Folder, Attachment as AttachIcon, Download, Trash, Plus, Xmark } from 'iconoir-react';
import { PremiumLimitModal } from './PremiumLimitModal';
import { StorageOverageBlockedModal } from './StorageOverageBlockedModal';
import { StorageWarningToast } from './StorageWarningToast';
import { ConfirmModal } from './ConfirmModal';
import { subscribeToCardModalEvents } from '../services/cardModalEventBus';
import { applyAttachmentAdded, applyAttachmentDeleted } from '../services/attachmentEvents';

const STORAGE_WARNING_SESSION_KEY = 'storage_warning_shown';

interface AttachmentListProps {
  cardId: number;
  canEdit: boolean;
  onAttachmentsChange?: () => void;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({ cardId, canEdit, onAttachmentsChange }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPremiumLimitModal, setShowPremiumLimitModal] = useState(false);
  const [showStorageOverageModal, setShowStorageOverageModal] = useState(false);
  const [storageWarning, setStorageWarning] = useState<{ usedMb: number; limitMb: number } | null>(null);
  const [pendingDuplicateFile, setPendingDuplicateFile] = useState<File | null>(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState<number | null>(null);

  useEffect(() => {
    loadAttachments();
  }, [cardId]);

  // Synchronise les pièces jointes via cardModalEventBus.
  useEffect(() => {
    const unsubscribe = subscribeToCardModalEvents((busEvent) => {
      if (busEvent.type === 'attachment-added') {
        setAttachments((prev) => applyAttachmentAdded(prev, cardId, busEvent.event));
      } else if (busEvent.type === 'attachment-deleted') {
        setAttachments((prev) => applyAttachmentDeleted(prev, cardId, busEvent.event));
      }
    });

    return unsubscribe;
  }, [cardId]);

  const loadAttachments = async () => {
    setIsLoading(true);
    try {
      const response = await attachmentApi.getAll(cardId);
      setAttachments(response.data);
      setError(null);
    } catch (err: any) {
      setError('Erreur lors du chargement des fichiers');
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setError(null);

    try {
      const response = await attachmentApi.upload(cardId, file);
      setAttachments(prev => [response.data, ...prev]);
      onAttachmentsChange?.();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      checkStorageWarning();
    } catch (err: any) {
      if (
        err.response?.status === 402 &&
        err.response?.data?.error === 'quota_exceeded' &&
        err.response?.data?.limit_type === 'storage'
      ) {
        setShowPremiumLimitModal(true);
      } else if (isStorageOverageBlocked(err)) {
        setShowStorageOverageModal(true);
      } else {
        setError(
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Erreur lors de l\'upload du fichier'
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Vérifier la taille (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setError('Le fichier est trop volumineux (50MB maximum)');
      return;
    }

    const duplicate = attachments.some(
      attachment =>
        attachment.filename === file.name &&
        attachment.size === file.size
    );

    if (duplicate) {
      setPendingDuplicateFile(file);
      return;
    }

    await uploadFile(file);
  };

  const handleCancelDuplicateUpload = () => {
    setPendingDuplicateFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleConfirmDuplicateUpload = async () => {
    const file = pendingDuplicateFile;
    setPendingDuplicateFile(null);
    if (file) {
      await uploadFile(file);
    }
  };

  // Avertissement stockage affiché une fois par session à partir de 90 %.
  const checkStorageWarning = async () => {
    if (sessionStorage.getItem(STORAGE_WARNING_SESSION_KEY)) return;

    try {
      const response = await subscriptionApi.getUsage();
      const storage = response.data?.overages?.storage;
      if (storage && storage.percentage >= 90) {
        sessionStorage.setItem(STORAGE_WARNING_SESSION_KEY, '1');
        setStorageWarning({ usedMb: storage.used, limitMb: storage.effective_limit });
      }
    } catch (err) {
      console.error('Erreur lors de la vérification du seuil de stockage', err);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const response = await attachmentApi.download(cardId, attachment.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Erreur lors du téléchargement');
    }
  };

  const handleImagePreview = async (attachment: Attachment) => {
    try {
      const response = await attachmentApi.download(cardId, attachment.id);
      const blob = new Blob([response.data], { type: attachment.mime_type });
      const url = window.URL.createObjectURL(blob);
      setPreviewImage(url);
    } catch (err: any) {
      setError('Erreur lors du chargement de l\'image');
    }
  };

  const closePreview = () => {
    if (previewImage) {
      window.URL.revokeObjectURL(previewImage);
      setPreviewImage(null);
    }
  };

  const handleDeleteClick = (attachmentId: number) => {
    setAttachmentToDelete(attachmentId);
  };

  const handleConfirmDelete = async () => {
    const attachmentId = attachmentToDelete;
    setAttachmentToDelete(null);
    if (attachmentId === null) return;

    try {
      await attachmentApi.delete(cardId, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      onAttachmentsChange?.();
    } catch (err: any) {
      setError('Erreur lors de la suppression');
    }
  };

  const getFileIcon = (fileType: string) => {
    const iconProps = { width: 24, height: 24, strokeWidth: 2 };
    switch (fileType) {
      case 'image':
        return <MediaImage {...iconProps} color="#0079BF" />;
      case 'video':
        return <MediaVideo {...iconProps} color="#6554C0" />;
      case 'document':
        return <Page {...iconProps} color="#FF5630" />;
      case 'archive':
        return <Folder {...iconProps} color="#FF991F" />;
      default:
        return <AttachIcon {...iconProps} color="#5E6C84" />;
    }
  };

  return (
    <div className="attachment-section">
      <div className="attachment-header">
        <h3>
          <AttachIcon width={20} height={20} strokeWidth={2} style={{ display: 'inline', marginRight: '8px' }} />
          Pièces jointes ({attachments.length})
        </h3>
        {canEdit && (
          <button
            className="btn btn-attachment-add"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Plus width={16} height={16} strokeWidth={2} />
            <span>{isUploading ? 'Upload en cours...' : 'Ajouter un fichier'}</span>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={isUploading}
        />
      </div>

      {error && <div className="attachment-error">{error}</div>}

      {isLoading ? (
        <div className="attachment-loading">Chargement...</div>
      ) : attachments.length === 0 ? (
        <div className="attachment-empty">Aucune pièce jointe</div>
      ) : (
        <div className="attachment-list">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="attachment-item">
              <div 
                className="attachment-icon"
                onClick={() => {
                  if (attachment.file_type === 'image') {
                    handleImagePreview(attachment);
                  }
                }}
                style={{ cursor: attachment.file_type === 'image' ? 'pointer' : 'default' }}
                title={attachment.file_type === 'image' ? 'Cliquez pour prévisualiser' : ''}
              >
                {getFileIcon(attachment.file_type)}
              </div>
              <div className="attachment-info">
                <div className="attachment-filename" title={attachment.filename}>
                  {attachment.filename}
                </div>
                <div className="attachment-meta">
                  {attachment.formatted_size} • Ajouté par {attachment.user.name}
                </div>
              </div>
              <div className="attachment-actions">
                <button
                  className="btn-attachment-download"
                  onClick={() => handleDownload(attachment)}
                  title="Télécharger"
                >
                  <Download width={18} height={18} strokeWidth={2} />
                </button>
                {canEdit && (
                  <button
                    className="btn-attachment-delete"
                    onClick={() => handleDeleteClick(attachment.id)}
                    title="Supprimer"
                  >
                    <Trash width={18} height={18} strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modale de prévisualisation d'image */}
      {previewImage && (
        <div className="image-preview-backdrop" onClick={closePreview}>
          <div className="image-preview-container" onClick={(e) => e.stopPropagation()}>
            <button className="image-preview-close" onClick={closePreview}>
              <Xmark width={20} height={20} strokeWidth={2} />
            </button>
            <img src={previewImage} alt="Prévisualisation" className="image-preview" />
          </div>
        </div>
      )}

      <PremiumLimitModal
        isOpen={showPremiumLimitModal}
        onClose={() => setShowPremiumLimitModal(false)}
        message={
          <>
            Vous avez atteint la limite de <strong>1 Go de stockage inclus</strong> dans votre offre gratuite.
          </>
        }
        premiumMessage={
          <>
            Les offres <strong>Premium</strong> incluent également <strong>1 Go de stockage</strong>,
            mais permettent d'utiliser du stockage supplémentaire en <strong>dépassement payant</strong>.
          </>
        }
      />

      <StorageOverageBlockedModal
        isOpen={showStorageOverageModal}
        onClose={() => setShowStorageOverageModal(false)}
      />

      {storageWarning && (
        <StorageWarningToast
          usedMb={storageWarning.usedMb}
          limitMb={storageWarning.limitMb}
          onClose={() => setStorageWarning(null)}
        />
      )}

      <ConfirmModal
        isOpen={attachmentToDelete !== null}
        title="Supprimer le fichier"
        message="Voulez-vous vraiment supprimer ce fichier ?"
        confirmText="Supprimer"
        onConfirm={handleConfirmDelete}
        onCancel={() => setAttachmentToDelete(null)}
        variant="danger"
      />

      <ConfirmModal
        isOpen={pendingDuplicateFile !== null}
        title="Fichier déjà présent"
        message={
          <>
            Un fichier portant le même nom et la même taille (
            <strong>{pendingDuplicateFile?.name}</strong>) semble déjà présent sur cette carte.
            Voulez-vous l'ajouter quand même ?
          </>
        }
        confirmText="Ajouter quand même"
        onConfirm={handleConfirmDuplicateUpload}
        onCancel={handleCancelDuplicateUpload}
        variant="warning"
      />

    </div>
  );
};
